import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import ToggleBar from "@mui/icons-material/Dehaze";
import CloseIcon from "@mui/icons-material/Close";
import CButton from "../../components/CButton";
import CLoader from "../../components/CLoader";
import { ReactComponent as ShareIcon } from "../../assets/images/share-icon-left.svg";
import { ReactComponent as ChatFileIcon } from "../../assets/images/chat-file-icon.svg";
import { ReactComponent as ChatSendIcon } from "../../assets/images/chat-send-icon.svg";
import CTextField from "../../components/CTextField";
import CDropZone from "../../components/CDropzone";
import {
  executeChatPrompt,
  setSelectedPromptType,
  addUserMessage,
  fetchPrompts,
  addPrompt,
  parseAndSaveCriticalSkills,
} from "../../slices/trainingStrategyInputSlice";
import { supabase } from "../../config/supabaseClient";
import { CHAT_CATEGORIES } from "../../constant/constants";
import CSimpleLoader from "../../components/CSimpleLoader";
import ReactMarkdown from "react-markdown";
import { toast } from "react-toastify";

const ChatPopup = ({
  isOpen,
  onClose,
  onResponse,
  activeSection,
  messages = [],
  onMessagesChange,
  fileContent = "",
  onSkillsSaved,
  industryTrends = "",
  workforceImpact = "",
  trainingResponse = "",
  goals = [],
  criticalSkills = [],
}) => {
  const dispatch = useDispatch();

  const {
    isLoading,
    selectedPromptType,
    prompts,
    promptsLoading,
    promptsError,
  } = useSelector((state) => state.trainingStrategyInput);

  const [file, setFile] = useState(null);
  const [inputMessage, setInputMessage] = useState("");
  const [latestAIResponse, setLatestAIResponse] = useState("");
  const [isProcessingMessage, setIsProcessingMessage] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const chatMessagesRef = useRef(null);

  useEffect(() => {
    setLatestAIResponse("");
  }, [isOpen, activeSection]);

  useEffect(() => {
    if (isOpen) {
      setIsInitializing(true);
      // First try to fetch existing prompts
      dispatch(fetchPrompts(activeSection))
        .then((result) => {
          return result;
        })
        .then((result) => {
          // console.log("Final prompts result:", result);
        })
        .catch((error) => {
          console.error("Error in prompt loading process:", error);
        })
        .finally(() => {
          setIsInitializing(false);
        });
    }
  }, [isOpen, dispatch, activeSection]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, isProcessingMessage]);

  // Auto-scroll when chat opens
  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [isOpen]);

  // Auto-scroll when AI processing starts
  useEffect(() => {
    if (isProcessingMessage) {
      scrollToBottom();
    }
  }, [isProcessingMessage]);

  // Auto-scroll when initialization completes
  useEffect(() => {
    if (!isInitializing && isOpen) {
      scrollToBottom();
    }
  }, [isInitializing, isOpen]);

  // Auto-scroll when user types (with debounce)
  useEffect(() => {
    if (inputMessage.trim()) {
      const timer = setTimeout(() => {
        scrollToBottom();
      }, 100); // Small delay to avoid excessive scrolling

      return () => clearTimeout(timer);
    }
  }, [inputMessage]);

  // useEffect(() => {
  //   const fetchLatestFile = async () => {
  //     console.log("Fetching latest file content...");
  //     try {
  //       const { data: latestFile, error } = await supabase
  //         .from("strategic_input")
  //         .select("*")
  //         .order("created_at", { ascending: false })
  //         .limit(1)
  //         .single();

  //       if (error) {
  //         console.error("Error fetching latest file:", error);
  //         return;
  //       }

  //       if (latestFile) {
  //         console.log(
  //           "File content fetched successfully:",
  //           latestFile.extracted_text?.substring(0, 100) + "..."
  //         );
  //         setFileContent(latestFile.extracted_text);
  //       } else {
  //         console.log("No file content found");
  //       }
  //     } catch (error) {
  //       console.error("Error in fetchLatestFile:", error);
  //     }
  //   };

  //   fetchLatestFile();
  // }, []);

  const handleDrop = (acceptedFiles) => {
    // console.log("File dropped:", acceptedFiles[0]?.name);
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  };

  const handleRemoveFile = () => {
    // console.log("File removed");
    setFile(null);
  };

  const handlePromptSelect = (prompt) => {
    dispatch(setSelectedPromptType(prompt.category));

    // Set the input message to the prompt label for display
    setInputMessage(prompt.label);

    // Call handleSubmit with the actual prompt text
    handleSubmit(prompt.prompt, prompt.label);
  };

  const handleSubmit = async (
    customPrompt = null,
    customDisplayMessage = null
  ) => {
    const messageToDisplay = customDisplayMessage || inputMessage.trim();
    const promptToSend = customPrompt || inputMessage.trim();

    if (!messageToDisplay || isProcessingMessage) {
      return;
    }

    // Check if file content is available for AI analysis
    if (!fileContent || fileContent.trim() === "") {
      const errorMessage = {
        role: "assistant",
        content:
          "Please upload a file first so I can analyze its content and provide relevant responses.",
      };
      const updatedMessages = [
        ...messages,
        { role: "user", content: messageToDisplay },
        errorMessage,
      ];
      onMessagesChange(updatedMessages);
      setInputMessage("");
      return;
    }
    
    setIsProcessingMessage(true);

    // Add user message to messages with actual prompt text for AI context
    const userMessage = {
      role: "user",
      content: customPrompt || inputMessage.trim(),
      alias: messageToDisplay,
    };
    const updatedMessages = [...messages, userMessage];
    onMessagesChange(updatedMessages);

    // Prepare the message with file content and conditional input values based on active section
    // Order: Strategic Input Text -> Industry Trends -> Workforce Impact -> Training -> Goals -> Critical Skills
    const goalsText =
      goals && goals.length > 0
        ? goals
            .map(
              (goal) =>
                `- ${goal.goal}${
                  goal.benefits ? ` (Benefits: ${goal.benefits})` : ""
                }${goal.timeline ? ` (Timeline: ${goal.timeline})` : ""}`
            )
            .join("\n")
        : "No goals defined";

      const criticalSkillsText =
            criticalSkills && criticalSkills.length > 0
              ? criticalSkills
                  .map((skill) => `- ${skill.skill_name} (Level: ${skill.level}, Importance: ${skill.importance})`)
                  .join("\n")
              : "No critical skills defined";

    // Build system prompt based on active section - only include inputs that come BEFORE the current section
    let systemContent = `You are an AI assistant analyzing a strategic input document. Here is the available context:

    Strategic Input Text:
    ${fileContent || "No strategic input text available"}`;

    // Add inputs based on active section (only those that come before the current section)
    if (activeSection === "industryTrends") {
      // Only Strategic Input Text (nothing before it)
      systemContent += `\n\nPlease analyze the strategic input document and provide insights on industry trends.`;
    } else if (activeSection === "workforceImpact") {
      // Strategic Input Text + Industry Trends
      systemContent += `\n\nIndustry Trends:
        ${industryTrends || "No industry trends data available"}

        Please analyze the strategic input and industry trends to provide insights on the impact on workforce and skills development.`;
            } else if (activeSection === "training") {
              // Strategic Input Text + Industry Trends + Workforce Impact
              systemContent += `\n\nIndustry Trends:
        ${industryTrends || "No industry trends data available"}

        Impact of These Trends on Workforce & Skills Development:
        ${workforceImpact || "No workforce impact data available"}

        Please analyze the strategic input, industry trends, and workforce impact to provide insights on training and development needs.`;
    } else if (activeSection === "criticalSkills") {
      // Strategic Input Text + Industry Trends + Workforce Impact + Training + Goals
      systemContent += `\n\nIndustry Trends:
        ${industryTrends || "No industry trends data available"}

        Impact of These Trends on Workforce & Skills Development:
        ${workforceImpact || "No workforce impact data available"}

        Training & Development Response:
        ${trainingResponse || "No training response data available"}

        Goals:
        ${goalsText}

        Please analyze all the above information to identify critical skills that align with the strategic input, industry trends, workforce impact, training needs, and organizational goals.`;
    } else if (activeSection === "strategicSummary") {
      // All inputs for strategic summary
      systemContent += `\n\nIndustry Trends:
        ${industryTrends || "No industry trends data available"}

        Impact of These Trends on Workforce & Skills Development:
        ${workforceImpact || "No workforce impact data available"}

        Training & Development Response:
        ${trainingResponse || "No training response data available"}

        Goals:
        ${goalsText}

        Critical Skills:
        ${criticalSkillsText || "No critical skills defined"}

        Please analyze all the above information to provide a comprehensive strategic summary.`;
    } else {
      // Default: only Strategic Input Text
      systemContent += `\n\nPlease analyze this content and provide insights based on the user's request.`;
    }

    const messageWithContent = [
      {
        role: "system",
        content: systemContent,
      },
      ...updatedMessages,
    ];

    try {
      const result = await dispatch(
        executeChatPrompt({
          prompt: promptToSend,
          message: messageWithContent,
        })
      );

      // Check if the action was rejected (error case)
      if (result.type && result.type.endsWith("/rejected")) {
        // Handle rejected action - error from API
        const errorMsg =
          result.payload?.error ||
          result.payload?.message ||
          "Sorry, I encountered an error. Please try again.";
        const errorMessage = {
          role: "assistant",
          content: `I'm having trouble connecting to the AI service. ${
            errorMsg.includes("No content received")
              ? "The AI service didn't return a response. This might be a temporary issue - please try again in a moment."
              : "Please try again."
          }`,
        };
        const newMessages = [...updatedMessages, errorMessage];
        onMessagesChange(newMessages);
      } else if (result.payload?.success === false) {
        // Handle API error response (when API returns success: false)
        const errorMsg =
          result.payload?.error ||
          result.payload?.message ||
          "Sorry, I encountered an error. Please try again.";
        const errorMessage = {
          role: "assistant",
          content: `I'm having trouble connecting to the AI service. ${
            errorMsg.includes("No content received")
              ? "The AI service didn't return a response. This might be a temporary issue - please try again in a moment."
              : "Please try again."
          }`,
        };
        const newMessages = [...updatedMessages, errorMessage];
        onMessagesChange(newMessages);
      } else {
        // Handle successful response
        const aiResponse = result.payload?.data?.content;
        if (aiResponse) {
          setLatestAIResponse(aiResponse);
          const aiMessage = { role: "assistant", content: aiResponse };
          const newMessages = [...updatedMessages, aiMessage];
          onMessagesChange(newMessages);
        } else {
          // Handle case where no content is returned
          const noContentMessage = {
            role: "assistant",
            content:
              "I received your message but couldn't generate a response. This might be a temporary issue with the AI service. Please try again in a moment.",
          };
          const newMessages = [...updatedMessages, noContentMessage];
          onMessagesChange(newMessages);
        }
      }
      setInputMessage("");
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      // Add error message to chat
      const errorMessage = {
        role: "assistant",
        content: "Sorry, I encountered an unexpected error. Please try again.",
      };
      const newMessages = [...updatedMessages, errorMessage];
      onMessagesChange(newMessages);
    } finally {
      setIsProcessingMessage(false);
    }
  };

  const handleShare = async () => {
    if (!latestAIResponse) return;

    // Special handling for Critical Skills - parse and save to database
    if (activeSection === "criticalSkills") {
      try {
        setIsProcessingMessage(true);
        const result = await dispatch(
          parseAndSaveCriticalSkills(latestAIResponse)
        );

        if (parseAndSaveCriticalSkills.fulfilled.match(result)) {
          const { created, updated, errors } = result.payload;
          let message = "";
          let toastMessage = "";

          if (created.length > 0) {
            message += `Successfully created ${created.length} skill(s). `;
            toastMessage += `Successfully Created ${created.length} skill(s). `;
          }
          if (updated.length > 0) {
            message += `Updated ${updated.length} existing skill(s). `;
            toastMessage += `Updated ${updated.length} skill(s). `;
          }
          if (errors.length > 0) {
            message += `Failed to process ${errors.length} skill(s). `;
            toastMessage += `Failed to process ${errors.length} skill(s). `;
          }

          // Show toast notification
          if (errors.length > 0) {
            toast.warning(
              toastMessage.trim() || "Some skills could not be processed."
            );
          } else {
            toast.success(toastMessage.trim() || "Skills saved successfully!");
          }

          // Add success message to chat
          const successMessage = {
            role: "assistant",
            content: message || "Skills processed successfully.",
          };
          const newMessages = [...messages, successMessage];
          onMessagesChange(newMessages);

          // Also call onResponse to update parent component
          onResponse(latestAIResponse, activeSection, newMessages);

          // Notify parent to refresh skills list
          if (onSkillsSaved) {
            onSkillsSaved();
          }
        } else {
          // Handle error
          const errorMsg = result.payload || "Unknown error";
          toast.error(`Failed to save skills: ${errorMsg}`);
          const errorMessage = {
            role: "assistant",
            content: `Failed to save skills: ${errorMsg}. Please try again.`,
          };
          const newMessages = [...messages, errorMessage];
          onMessagesChange(newMessages);
        }
      } catch (error) {
        console.error("Error in handleShare for critical skills:", error);
        const errorMessage = {
          role: "assistant",
          content: "An error occurred while saving skills. Please try again.",
        };
        const newMessages = [...messages, errorMessage];
        onMessagesChange(newMessages);
      } finally {
        setIsProcessingMessage(false);
      }
    } else {
      // For other sections, use the default behavior
      onResponse(latestAIResponse, activeSection, messages);
    }

    onClose(); // Close the chat after sharing
  };

  const scrollToBottom = () => {
    if (chatMessagesRef.current) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        chatMessagesRef.current?.scrollTo({
          top: chatMessagesRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 50);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      // console.log("Enter key pressed, submitting message");
      handleSubmit();
    }
  };

  return (
    <div
      className={`fixed bottom-0 h-[calc(100vh-70px)] right-0 w-[640px] bg-white rounded-s-lg shadow-xl p-0 z-50 border border-solid border-blue-200 transition-all duration-300 ${
        isOpen
          ? "translate-x-0 opacity-100"
          : "translate-x-full opacity-0 pointer-events-none"
      }`}
    >
      <div className="grid grid-cols-1 grid-rows-[auto,1fr,auto] h-full">
        <div className="flex justify-between items-center mb-2 p-3 border-b border-blue-200">
          <CButton className="!h-[32px] !w-[32px] !p-2">
            {/* <ToggleBar className="text-gray-700" /> */}
          </CButton>
          <div className="flex items-center gap-2">
            <span className="!font-[Poppins] ai-gradient-text text-3xl leading-3xl font-semibold">
              {Object.values(CHAT_CATEGORIES).find(
                (category) => category.id === activeSection
              )?.label || "iFundi.ai"}
            </span>
            {promptsLoading && <CLoader size="small" local={true} />}
            {/* File content indicator */}
          </div>
          <CButton onClick={onClose} className="!h-[32px] !w-[32px] !p-2">
            <CloseIcon className="text-gray-700" />
          </CButton>
        </div>

        <div
          ref={chatMessagesRef}
          className="h-full flex flex-col overflow-y-auto p-4 text-sm text-gray-700 scroll-smooth"
        >
          {isInitializing ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3">
                <CLoader size="medium" local={true} />
                <p className="text-gray-500">Initializing chat...</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {(messages || [])
                .filter((msg) => msg && msg.role && msg.content)
                .map((message, index) => (
                  <div
                    key={index}
                    className={`${
                      message.role === "user"
                        ? "chat-right-content p-3 rounded-lg   max-w-[80%] ml-auto bg-[#e6f4fb]"
                        : "chat-left-content bg-[linear-gradient(90deg,_#dc0451_31.18%,_#ffb612)] p-[1px] rounded-lg max-w-[80%] mr-auto"
                    }`}
                  >
                    <div
                      className={`text-sm ${
                        message.role === "user"
                          ? "text-gray-700"
                          : "text-gray-700 p-3 !bg-white rounded-lg"
                      }`}
                    >
                      <ReactMarkdown>
                        {message.alias || message.content}
                      </ReactMarkdown>
                    </div>
                  </div>
                ))}

              {/* AI Processing Indicator */}
              {isProcessingMessage && <CSimpleLoader />}
            </div>
          )}
        </div>

        <div className="border-t border-blue-200">
          <ul className="flex gap-2 overflow-x-auto w-full p-2">
            {promptsLoading ? (
              <li className="flex items-center gap-2 text-gray-500 p-2">
                <CLoader size="small" local={true} />
                Loading prompts...
              </li>
            ) : promptsError ? (
              <li className="text-red-500 p-2">
                Error loading prompts: {promptsError}
              </li>
            ) : (
              prompts.map((prompt) => (
                <li
                  key={prompt.id}
                  onClick={() => handlePromptSelect(prompt)}
                  className={`border border-blue-500 text-blue-500 rounded-lg p-2 text-sm leading-sm whitespace-nowrap cursor-pointer hover:bg-blue-50 transition-colors ${
                    selectedPromptType === prompt.category ? "bg-blue-50" : ""
                  }`}
                >
                  {prompt.label}
                </li>
              ))
            )}
          </ul>
          <div className="border-t border-blue-200 p-4 flex gap-2 items-start">
            <CButton
              className={`bg-blue-500 hover:bg-blue-700 lg:w-[50px] lg:!h-[50px] !p-3 ${
                !latestAIResponse ? "opacity-50 cursor-not-allowed" : ""
              }`}
              onClick={handleShare}
              disabled={!latestAIResponse}
            >
              <ShareIcon className="w-[24px] h-[24px]" />
            </CButton>
            <div className="relative gen-chat-field w-full">
              <CTextField
                name="chatField"
                placeholder={
                  isInitializing
                    ? "Initializing..."
                    : isProcessingMessage
                    ? "AI is processing..."
                    : // : "Type message"
                    !fileContent || fileContent.trim() === ""
                    ? "Upload a file first to analyze its content"
                    : "Type message to analyze the uploaded file"
                }
                multiline
                minRows={1}
                maxRows={3}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isProcessingMessage || isInitializing}
              />
              {/* <CDropZone
                onDrop={handleDrop}
                file={file}
                uploadIcon={<ChatFileIcon />}
                onRemoveFile={handleRemoveFile}
              /> */}
            </div>
            <CButton
              className={`lg:!min-w-[50px] !min-w-[42px] lg:w-[50px] lg:h-[50px] w-[42px] h-[42px] !rounded-full ai-btn ${
                isProcessingMessage || isInitializing
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              onClick={handleSubmit}
              disabled={
                isProcessingMessage || isInitializing || !inputMessage.trim()
              }
            >
              {isProcessingMessage ? (
                <CLoader size="small" local={true} />
              ) : (
                <ChatSendIcon className="w-[20px] h-[20px] text-grey-700" />
              )}
            </CButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPopup;
